<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { sessionStore } from "@/stores/Session";
import { UserApiClient } from "@/client/requests";
import { useProductStore } from "@/stores/Product";
import { useAvailablePermissionsStore } from "@/stores/Permission";
import { useLicenseTemplateStore } from "@/stores/LicenseTemplate";

const session = sessionStore();
const router = useRouter();
const userPermissions = ref<Array<string>>([]);
const username = ref(session.session?.user?.email || "");
const apiClient = new UserApiClient();

onMounted(async () => {
  const productStore = useProductStore();
  const permissionStore = useAvailablePermissionsStore();
  const licenseTemplateStore = useLicenseTemplateStore();

  session.loadSession();
  if (session.session) {
    userPermissions.value = Object.keys(
      session.session?.user?.permissions || {}
    );

    await Promise.all([
      productStore.fetchProducts(),
      productStore.fetchProductFamilies(),
      permissionStore.fetchActivePermissions(),
      licenseTemplateStore.fetchTemplates(),
    ]);
  }
});

/**
 * Logs the user out of the application.
 * This function is asynchronous and may involve clearing user session data,
 * revoking authentication tokens, or redirecting the user to a login page.
 */
async function logout() {
  await apiClient.logout();
  session.clearSession();
  userPermissions.value = [];
  router.push("/login");
}

/**
 * Updates the state or properties of the menubar component.
 * This function is responsible for handling any changes or updates
 * required for the menubar, such as dynamic adjustments or state management.
 */
function updateMenubar() {
  if (session.session) {
    userPermissions.value = Object.keys(
      session.session?.user?.permissions || {}
    );
  } else {
    userPermissions.value = [];
  }
}

defineExpose({
  updateMenubar,
});
</script>
<template>
  <nav class="navbar navbar-expand-lg shadow-sm">
    <div class="container-xxl">
      <router-link to="/" class="navbar-brand px-2">
        Gerenciador de Licenças
      </router-link>
      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#mainNavbar"
      >
        <span class="navbar-toggler-icon" />
      </button>

      <div id="mainNavbar" class="collapse navbar-collapse">
        <ul class="navbar-nav me-auto">
          <li
            v-if="userPermissions.includes('license')"
            class="nav-item dropdown"
          >
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="bi bi-file-earmark-text" />
              Ativos
            </a>
            <ul class="dropdown-menu">
              <li>
                <router-link :to="{ name: 'licenses' }" class="dropdown-item">
                  Ativos
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'assetEdit' }" class="dropdown-item">
                  Novo Ativo
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'underConstruction' }" class="dropdown-item">
                  Templates
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'assetBatches' }"
                  class="dropdown-item"
                >
                  Lotes de Ativos
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'underConstruction' }" class="dropdown-item">
                  QR Code
                </router-link>
              </li>
            </ul>
          </li>
          <li v-if="userPermissions.includes('permission')" class="nav-item dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="bi bi-lock" />
              Permissões
            </a>
            <ul class="dropdown-menu">
              <li>
                <router-link :to="{ name: 'permissions' }" class="dropdown-item">
                  Permissões
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'permissionRules' }" class="dropdown-item">
                  Regras de Permissão
                </router-link>
              </li>
            </ul>
          </li>
          <li
            v-if="userPermissions.includes('product')"
            class="nav-item dropdown"
          >
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i class="bi bi-cpu" />
              Produtos
            </a>
            <ul class="dropdown-menu">
              <li>
                <router-link
                  :to="{ name: 'productFamilies' }"
                  class="dropdown-item"
                >
                  Famílias de Produtos
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'products' }" class="dropdown-item">
                  Produtos
                </router-link>
              </li>
            </ul>
          </li>
          <li v-if="userPermissions.includes('client')" class="nav-item">
            <router-link :to="{ name: 'underConstruction' }" class="nav-link">
              <i class="bi bi-building" />
              Clientes
            </router-link>
          </li>
          <li v-if="userPermissions.includes('user')" class="nav-item">
            <router-link :to="{ name: 'users' }" class="nav-link">
              <i class="bi bi-person" />
              Usuários
            </router-link>
          </li>
        </ul>
        <ul v-if="session.session" class="navbar-nav me-3">
          <li class="nav-item">
            <a class="nav-link text-primary fw-medium" href="#">
              <i class="bi bi-person-fill" />
              {{ session.session.user.email }}
            </a>
          </li>
        </ul>
        <ul v-if="session.session" class="navbar-nav">
          <li class="nav-item">
            <a class="nav-link" href="#" @click="logout">
              <i class="bi bi-door-open-fill" />
              Logout
            </a>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>
<style lang="scss">
@import "~bootstrap/scss/bootstrap";

.navbar-nav .nav-link.active {
  border-bottom: 0.125rem solid $primary;
}

@include media-breakpoint-down(lg) {
  .navbar-nav .nav-link {
    padding-left: 0.625rem !important;
  }
}
</style>
